import { atom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import request from 'lib/request';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';
import type {
  MatchReviewResponse,
  WellLifecycleResponse,
  WellOverviewResponse,
} from '../types-well';

// RC Well Review data layer — mirror of DO's atoms/wells:
//   • the Overview list lives in a MODULE-LEVEL atom so the shell nav, the
//     overview table, and Match Review share one piece of state and any mutation
//     can refetch to update them;
//   • the Match Review detail uses `createResourceAtom` (auto-fetches on mount);
//   • confirm / revert / change-match / rerun / add / edit / remove / restore are
//     uniform { url, method, data } atoms from the SHARED buildSectionMutationAtom.
// The drill-in SELECTION state is document-scoped in `./layout`.

// ── Overview (GET /documents/:documentId/royalty_check_structuring/wells) ─────

export const wellOverviewDataAtom = atom<WellOverviewResponse | null>(null);
export const wellOverviewLoadingAtom = atom(false);
// Non-null message means the last fetch failed — the section shows an error
// banner instead of an empty table, so a 5xx/network failure is never confused
// with a check that genuinely has no staged wells.
export const wellOverviewErrorAtom = atom<string | null>(null);

// The data atom is module-level (shared across RC documents), so a stale in-flight
// response — a prior document's fetch resolving after a switch, or an older reload
// superseded by a newer one — must not commit over the current list. Dropped
// unless it is still the latest requested href.
const wellOverviewRequestedHrefAtom = atom<string | undefined>(undefined);

export const fetchWellOverviewAtom = atom(
  null,
  async (get, set, href: string | undefined) => {
    if (!href) return;
    set(wellOverviewRequestedHrefAtom, href);
    set(wellOverviewLoadingAtom, true);
    set(wellOverviewErrorAtom, null);
    try {
      const response = await request.get<WellOverviewResponse>(href);
      if (get(wellOverviewRequestedHrefAtom) !== href) return;
      if ('error' in response) {
        console.error('Failed to fetch RC well overview:', response.error);
        set(wellOverviewErrorAtom, 'Unable to load wells.');
      } else {
        set(wellOverviewDataAtom, response);
      }
    } catch (error) {
      if (get(wellOverviewRequestedHrefAtom) !== href) return;
      console.error('Failed to fetch RC well overview:', error);
      set(wellOverviewErrorAtom, 'Unable to load wells.');
    } finally {
      if (get(wellOverviewRequestedHrefAtom) === href) {
        set(wellOverviewLoadingAtom, false);
      }
    }
  }
);

export const wellOverviewUrl = (documentId: number | string) =>
  `/documents/${documentId}/royalty_check_structuring/wells`;

// ── Match Review detail (GET .../royalty_check_structuring/wells/:id) ─────────
//
// :id is a NUMERIC RoyaltyCheckTarget PK (the controller does
// `@document.royalty_check_targets.find(params[:id])`).

export const createWellDetailResourceAtom = (href: string | undefined) =>
  createResourceAtom<MatchReviewResponse>(href);

export const wellDetailUrl = (
  documentId: number | string,
  wellId: number | string
) => `/documents/${documentId}/royalty_check_structuring/wells/${wellId}`;

// ── Lifecycle mutations (SHARED buildSectionMutationAtom) ─────────────────────

/** Confirm a chosen suggestion (POST resources.confirmation.href). */
export const confirmMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Revert a confirmation (DELETE resources.confirmation.href). */
export const revertMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Change Match — pick existing / create imported (POST resources.matchCandidate.href). */
export const changeMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Rerun the well-matching agent (POST resources.suggestionRerun.href). */
export const rerunSuggestionsAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Add a well — existing or imported (POST .../royalty_check_structuring/wells). */
export const addWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Edit a staged well (PATCH .../royalty_check_structuring/wells/:id). */
export const editWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Remove a well (DELETE .../royalty_check_structuring/wells/:id). */
export const removeWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Restore a removed well (POST .../royalty_check_structuring/wells/:id/restoration). */
export const restoreWellAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();
